import { useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useLocationTracking } from '@/hooks/useLocationTracking';
import { subscribeToPushNotifications } from '@/lib/services/pushNotificationService';
import { supabase } from '@/integrations/supabase/client';

export function LocationTrackingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const { requestPermission, startTracking } = useLocationTracking({
    continuous: true,
    autoStart: false, // Don't auto-start, wait for user permission
    minDistanceMeters: 100,
    updateIntervalMs: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (!user?.id) return;

    // Check if user has GPS notifications enabled
    const checkAndEnableTracking = async () => {
      try {
        // Check if user has premium GPS subscription or push notifications enabled
        const { data: prefs, error: prefsError } = await supabase
          .from('notification_preferences')
          .select('push_enabled, push_truck_nearby, premium_real_time_gps')
          .eq('user_id', user.id)
          .maybeSingle();

        if (prefsError) {
          console.error('Error fetching notification preferences:', prefsError);
          // Continue execution - we'll default to not enabling if we can't check
        }

        // Check if user has active GPS premium addon
        const { data: hasPremium, error: premiumError } = await supabase
          .from('user_premium_addons')
          .select('id')
          .eq('user_id', user.id)
          .eq('addon_name', 'premium_real_time_gps')
          .eq('status', 'active')
          .maybeSingle();

        if (premiumError) {
          console.error('Error fetching premium addons:', premiumError);
          // Continue execution - we'll default to not enabling if we can't check
        }

        // Only enable if user has push notifications enabled or premium GPS
        const shouldEnable = 
          (prefs?.push_enabled && prefs?.push_truck_nearby) || 
          hasPremium ||
          prefs?.premium_real_time_gps;

        if (!shouldEnable) {
          return; // User hasn't enabled GPS notifications
        }

        // Request location permission
        const hasPermission = await requestPermission();
        
        if (hasPermission) {
          // Start continuous tracking
          startTracking();
          
          // Also subscribe to push notifications if not already subscribed
          await subscribeToPushNotifications(user.id);
        }
      } catch (error) {
        console.error('Error setting up location tracking:', error);
      }
    };

    checkAndEnableTracking();
  }, [user?.id, requestPermission, startTracking]);

  return <>{children}</>;
}

